<template>
  <div
    class="status-indicator"
    :class="{ 'status-indicator--with-separator': showSeparator }"
    data-testid="status-indicator"
  >
    <div
      class="status-indicator__content"
      :class="sizePaddingClass"
      data-testid="status-indicator__content"
    >
      <div
        class="status-indicator__square"
        :class="squareSizeClass"
        :style="indicatorStyle"
        aria-hidden="true"
        data-testid="status-indicator__indicator"
      ></div>
      <span
        class="status-indicator__label"
        :class="labelSizeClass"
        data-testid="status-indicator__label"
      >
        {{ effectiveLabel }}
      </span>
    </div>
  </div>
</template>

<script>
import {
  STATUS_CONFIG,
  STATUS_VARIANTS,
  DEFAULT_STATUS_VARIANT,
} from './utils/status-constants';

export default {
  name: 'StatusIndicator',
  props: {
    /**
     * The status variant to display.
     * Must be one of the values in STATUS_VARIANTS.
     */
    variant: {
      type: String,
      required: true,
      validator(value) {
        return Object.values(STATUS_VARIANTS).includes(value);
      },
    },
    /**
     * Custom label to override the default status label.
     */
    customLabel: {
      type: String,
      default: '',
    },
    /**
     * Whether to show a separator line below the indicator.
     */
    showSeparator: {
      type: Boolean,
      default: false,
    },
    /**
     * Size of the indicator.
     * Options: 'sm', 'md', 'lg'.
     */
    size: {
      type: String,
      default: 'md',
      validator(value) {
        return ['sm', 'md', 'lg'].includes(value);
      },
    },
  },
  computed: {
    /**
     * Resolve the configuration for the provided status variant.
     * Falls back to DEFAULT_STATUS_VARIANT if variant is not found.
     */
    variantConfig() {
      return STATUS_CONFIG[this.variant] || STATUS_CONFIG[DEFAULT_STATUS_VARIANT];
    },
    /**
     * Determine the fallback-aware label used by the component.
     */
    effectiveLabel() {
      if (this.customLabel) {
        return this.customLabel;
      }
      return this.variantConfig.label;
    },
    /**
     * Inline style for the colored indicator.
     */
    indicatorStyle() {
      return {
        backgroundColor: this.variantConfig.color,
        boxShadow: `0 4px 12px ${this.variantConfig.shadow || 'transparent'}`,
      };
    },
    /**
     * Padding class based on size selection.
     */
    sizePaddingClass() {
      switch (this.size) {
        case 'sm':
          return 'status-indicator__content--sm';
        case 'lg':
          return 'status-indicator__content--lg';
        default:
          return 'status-indicator__content--md';
      }
    },
    /**
     * Square size modifier class.
     */
    squareSizeClass() {
      return `status-indicator__square--${this.size}`;
    },
    /**
     * Label size modifier class.
     */
    labelSizeClass() {
      return `status-indicator__label--${this.size}`;
    },
  },
};
</script>
 
<style scoped>
.status-indicator {
  display: flex;
  flex-direction: column;
  width: 100%;
}
 
.status-indicator--with-separator {
  border-bottom: 1px solid rgba(147, 51, 234, 0.25);
}
 
.status-indicator__content {
  display: flex;
  align-items: center;
  gap: 8px;
}
 
.status-indicator__square {
  flex-shrink: 0;
  border-radius: 4px;
}
 
.status-indicator__square--sm {
  width: 10px;
  height: 10px;
  border-radius: 2px;
}
 
.status-indicator__square--md {
  width: 14px;
  height: 14px;
  border-radius: 4px;
}
 
.status-indicator__square--lg {
  width: 16px;
  height: 16px;
  border-radius: 6px;
}
 
.status-indicator__label {
  /* letter-spacing: -0.01em; */
  font-weight: 400;
}
 
</style>
 
 